import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, User, Edit, Trash2, Calendar, Archive, RotateCcw, ChevronDown, LayoutGrid, List, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MesGroupes = () => {
  const { getPatientLabel, getPatientLabelPlural } = useSettings();
  const [groupes, setGroupes] = useState([]);
  const [archivedGroupes, setArchivedGroupes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [patients, setPatients] = useState([]);
  const [isCreateGroupeOpen, setIsCreateGroupeOpen] = useState(false);
  const [isEditGroupeOpen, setIsEditGroupeOpen] = useState(false);
  const [editingGroupe, setEditingGroupe] = useState(null);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [groupeToArchive, setGroupeToArchive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Form states
  const [newGroupe, setNewGroupe] = useState({
    nom: '',
    description: '',
    couleur: '#3B82F6'
  });

  const [editGroupe, setEditGroupe] = useState({
    nom: '',
    description: '',
    couleur: '#3B82F6'
  });

  // Sort groupes alphabetically
  const sortedGroupes = useMemo(() => {
    return [...groupes].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr'));
  }, [groupes]);

  // Sort archived groupes alphabetically
  const sortedArchivedGroupes = useMemo(() => {
    return [...archivedGroupes].sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr'));
  }, [archivedGroupes]);

  // Load data
  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      
      const [groupesRes, archivedGroupesRes, patientsRes] = await Promise.all([
        axios.get(`${API}/groupes`), // Non-archived groups by default
        axios.get(`${API}/groupes?include_archived=true`), // All groups to filter archived ones
        axios.get(`${API}/patients`)
      ]);
      
      const data = {
        groupes: groupesRes.data,
        archivedGroupes: archivedGroupesRes.data.filter(g => g.archived),
        patients: patientsRes.data
      };
      
      setGroupes(data.groupes);
      setArchivedGroupes(data.archivedGroupes);
      setPatients(data.patients);
      
      try {
        sessionStorage.setItem('cachedGroupesData', JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Could not cache groupes data:', e);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des groupes:', error);
      toast.error('Erreur lors du chargement des groupes');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    try {
      const cached = sessionStorage.getItem('cachedGroupesData');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        
        if (age < 5 * 60 * 1000) {
          setGroupes(data.groupes);
          setArchivedGroupes(data.archivedGroupes);
          setPatients(data.patients);
          setLoading(false);
          loadData(true);
          return;
        }
      }
    } catch (e) {
      console.warn('Could not load cached groupes:', e);
    }
    
    loadData();
  }, []);

  // Create groupe
  const handleCreateGroupe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/groupes`, newGroupe);
      toast.success('Groupe créé avec succès');
      setIsCreateGroupeOpen(false);
      setNewGroupe({ nom: '', description: '', couleur: '#3B82F6' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création du groupe');
    }
  };

  // Edit groupe
  const handleEditGroupe = (groupe) => {
    setEditingGroupe(groupe);
    setEditGroupe({
      nom: groupe.nom,
      description: groupe.description || '',
      couleur: groupe.couleur || '#3B82F6'
    });
    setIsEditGroupeOpen(true);
  };

  // Update groupe
  const handleUpdateGroupe = async (e) => {
    e.preventDefault();
    if (!editingGroupe) return;

    try {
      await axios.put(`${API}/groupes/${editingGroupe.id}`, editGroupe);
      toast.success('Groupe modifié avec succès');
      setIsEditGroupeOpen(false);
      setEditingGroupe(null);
      setEditGroupe({ nom: '', description: '', couleur: '#3B82F6' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification du groupe');
    }
  };

  const cancelEditGroupe = () => {
    setIsEditGroupeOpen(false);
    setEditingGroupe(null);
    setEditGroupe({ nom: '', description: '', couleur: '#3B82F6' });
  };

  // Archive groupe
  const handleArchiveGroupe = (groupe) => {
    setGroupeToArchive(groupe);
    setIsArchiveConfirmOpen(true);
  };

  const confirmArchiveGroupe = async () => {
    if (!groupeToArchive) return;

    try {
      const response = await axios.post(`${API}/groupes/${groupeToArchive.id}/archive`);
      toast.success(`Groupe archivé avec succès (${response.data.archived_patients} élèves, ${response.data.archived_notes} notes)`);
      setIsArchiveConfirmOpen(false);
      setGroupeToArchive(null);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage du groupe');
    }
  };

  const cancelArchiveGroupe = () => {
    setIsArchiveConfirmOpen(false);
    setGroupeToArchive(null);
  };

  // Restore groupe
  const handleRestoreGroupe = async (groupe) => {
    if (window.confirm(`Êtes-vous sûr de vouloir restaurer le groupe "${groupe.nom}" et tous ses éléments archivés ?`)) {
      try {
        const response = await axios.post(`${API}/groupes/${groupe.id}/unarchive`);
        toast.success(`Groupe restauré avec succès (${response.data.restored_patients} élèves, ${response.data.restored_notes} notes)`);
        loadData();
      } catch (error) {
        toast.error('Erreur lors de la restauration du groupe');
      }
    }
  };

  // Get patients count for a group
  const getPatientsCount = (groupeId) => {
    return patients.filter(p => p.groupe_id === groupeId).length;
  };

  const getNotesCount = (groupeId) => {
    const groupePatients = patients.filter(p => p.groupe_id === groupeId);
    return groupePatients.reduce((total, patient) => total + (patient.notes_count || 0), 0);
  };

  return (
    <div className="py-6">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Mes Groupes</h2>
            <p className="text-gray-600 mt-1">Organisez vos {getPatientLabelPlural()} en groupes</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* View mode toggle */}
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="flex items-center gap-1"
                data-testid="view-grid-btn"
              >
                <LayoutGrid className="h-4 w-4" />
                Grille
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="flex items-center gap-1"
                data-testid="view-list-btn"
              >
                <List className="h-4 w-4" />
                Liste
              </Button>
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center gap-2"
              data-testid="toggle-archived-groups"
            >
              {showArchived ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {showArchived ? 'Masquer' : 'Afficher'} les archivés ({archivedGroupes.length})
            </Button>
            
            <Dialog open={isCreateGroupeOpen} onOpenChange={setIsCreateGroupeOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2" data-testid="create-group-btn">
                  <Plus className="h-4 w-4" />
                  Nouveau Groupe
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer un nouveau groupe</DialogTitle>
                <DialogDescription>
                  Organisez vos {getPatientLabelPlural()} en groupes personnalises
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateGroupe} className="space-y-4">
                <div>
                  <Label htmlFor="groupe-nom">Nom du groupe *</Label>
                  <Input
                    id="groupe-nom"
                    value={newGroupe.nom}
                    onChange={(e) => setNewGroupe({...newGroupe, nom: e.target.value})}
                    placeholder="ex: Classe de CE2, Groupe thérapie..."
                    required
                    data-testid="group-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="groupe-description">Description</Label>
                  <Textarea
                    id="groupe-description"
                    value={newGroupe.description}
                    onChange={(e) => setNewGroupe({...newGroupe, description: e.target.value})}
                    placeholder="Description du groupe..."
                    data-testid="group-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="groupe-couleur">Couleur</Label>
                  <Input
                    id="groupe-couleur"
                    type="color"
                    value={newGroupe.couleur}
                    onChange={(e) => setNewGroupe({...newGroupe, couleur: e.target.value})}
                    data-testid="group-color-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-group-btn">Créer le groupe</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Edit Group Modal */}
        <Dialog open={isEditGroupeOpen} onOpenChange={setIsEditGroupeOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier le groupe {editingGroupe ? editingGroupe.nom : ''}</DialogTitle>
              <DialogDescription>
                Modifiez les informations du groupe
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateGroupe} className="space-y-4">
              <div>
                <Label htmlFor="edit-groupe-nom">Nom du groupe *</Label>
                <Input
                  id="edit-groupe-nom"
                  value={editGroupe.nom}
                  onChange={(e) => setEditGroupe({...editGroupe, nom: e.target.value})}
                  placeholder="ex: Classe de CE2, Groupe thérapie..."
                  required
                  data-testid="edit-group-name-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-groupe-description">Description</Label>
                <Textarea
                  id="edit-groupe-description"
                  value={editGroupe.description}
                  onChange={(e) => setEditGroupe({...editGroupe, description: e.target.value})}
                  placeholder="Description du groupe..."
                  data-testid="edit-group-description-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-groupe-couleur">Couleur</Label>
                <Input
                  id="edit-groupe-couleur"
                  type="color"
                  value={editGroupe.couleur}
                  onChange={(e) => setEditGroupe({...editGroupe, couleur: e.target.value})}
                  data-testid="edit-group-color-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="update-group-btn">Modifier</Button>
                <Button type="button" variant="outline" onClick={cancelEditGroupe} data-testid="cancel-edit-group-btn">Annuler</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Groups List */}
        {/* Groups Display - Grid or List View */}
        <div className={viewMode === 'grid' ? 'grid gap-6 md:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-4'}>
          {loading ? (
            <div className={viewMode === 'grid' ? 'col-span-full text-center py-8' : 'text-center py-8'}>Chargement...</div>
          ) : sortedGroupes.length === 0 ? (
            <div className={viewMode === 'grid' ? 'col-span-full text-center py-8 text-gray-500' : 'text-center py-8 text-gray-500'}>
              Aucun groupe trouvé. Créez votre premier groupe pour commencer.
            </div>
          ) : (
            sortedGroupes.map(groupe => (
              <Card key={groupe.id} className="hover:shadow-lg transition-shadow" data-testid={`group-${groupe.id}`}>
                <CardHeader>
                  <div className={viewMode === 'list' ? 'flex items-center justify-between' : 'flex items-center justify-between'}>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{backgroundColor: groupe.couleur}}
                      ></div>
                      <CardTitle className="text-lg">
                        <Link 
                          to={`/groupes/${groupe.id}`}
                          className="hover:text-blue-600 transition-colors cursor-pointer"
                        >
                          {groupe.nom}
                        </Link>
                      </CardTitle>
                      {viewMode === 'list' && (
                        <div className="flex items-center gap-4 ml-8">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <User className="h-4 w-4" />
                            <span className="font-medium">{getPatientsCount(groupe.id)}</span> {getPatientLabel()}(s)
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <FileText className="h-4 w-4" />
                            <span className="font-medium">{getNotesCount(groupe.id)}</span> note(s)
                          </div>
                          <div className="text-sm text-gray-500">
                            Créé le {new Date(groupe.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditGroupe(groupe)}
                        className="text-blue-600 hover:text-blue-700"
                        data-testid={`edit-group-${groupe.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleArchiveGroupe(groupe)}
                        className="text-amber-600 hover:text-amber-700"
                        data-testid={`archive-group-${groupe.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {groupe.description && viewMode === 'grid' && (
                    <CardDescription>{groupe.description}</CardDescription>
                  )}
                  {groupe.description && viewMode === 'list' && (
                    <CardDescription className="mt-2">{groupe.description}</CardDescription>
                  )}
                </CardHeader>
                {viewMode === 'grid' && (
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          <span className="font-medium">{getPatientsCount(groupe.id)}</span> {getPatientLabel()}(s)
                        </div>
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          <span className="font-medium">{getNotesCount(groupe.id)}</span> note(s)
                        </div>
                      </div>
                      <div>
                        Créé le {new Date(groupe.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Archived Groups Section */}
        {showArchived && (
          <div className="mt-12">
            <h3 className="text-xl font-semibold text-gray-700 mb-6 flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Groupes Archivés ({archivedGroupes.length})
            </h3>
            <div className={viewMode === 'grid' ? 'grid gap-6 md:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-4'}>
              {sortedArchivedGroupes.length === 0 ? (
                <div className={viewMode === 'grid' ? 'col-span-full text-center py-8 text-gray-500' : 'text-center py-8 text-gray-500'}>
                  Aucun groupe archivé.
                </div>
              ) : (
                sortedArchivedGroupes.map(groupe => (
                  <Card key={groupe.id} className="hover:shadow-lg transition-shadow opacity-75 border-dashed" data-testid={`archived-group-${groupe.id}`}>
                    <CardHeader>
                      <div className={viewMode === 'list' ? 'flex items-center justify-between' : 'flex items-center justify-between'}>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full opacity-50" 
                            style={{backgroundColor: groupe.couleur}}
                          ></div>
                          <CardTitle className="text-lg text-gray-600">
                            <Link 
                              to={`/groupes/${groupe.id}`}
                              className="hover:text-blue-600 transition-colors cursor-pointer"
                            >
                              {groupe.nom}
                            </Link>
                          </CardTitle>
                          {viewMode === 'list' && (
                            <div className="flex items-center gap-4 ml-8">
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <Archive className="h-4 w-4" />
                                Archivé
                              </div>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <User className="h-4 w-4" />
                                <span className="font-medium">{getPatientsCount(groupe.id)}</span> {getPatientLabel()}(s)
                              </div>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <FileText className="h-4 w-4" />
                                <span className="font-medium">{getNotesCount(groupe.id)}</span> note(s)
                              </div>
                            </div>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleRestoreGroupe(groupe)}
                          className="text-green-600 hover:text-green-700"
                          data-testid={`restore-group-${groupe.id}`}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </div>
                      {groupe.description && viewMode === 'grid' && (
                        <CardDescription className="text-gray-500">{groupe.description}</CardDescription>
                      )}
                      {groupe.description && viewMode === 'list' && (
                        <CardDescription className="text-gray-500 mt-2">{groupe.description}</CardDescription>
                      )}
                    </CardHeader>
                    {viewMode === 'grid' && (
                      <CardContent>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1">
                              <Archive className="h-4 w-4" />
                              Groupe archivé
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              <span className="font-medium">{getPatientsCount(groupe.id)}</span> {getPatientLabel()}(s)
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="h-4 w-4" />
                              <span className="font-medium">{getNotesCount(groupe.id)}</span> note(s)
                            </div>
                          </div>
                          <div>
                            Archivé
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Archive Confirmation Modal */}
        <Dialog open={isArchiveConfirmOpen} onOpenChange={setIsArchiveConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <Archive className="h-5 w-5" />
                Archiver le groupe "{groupeToArchive?.nom}"
              </DialogTitle>
              <DialogDescription>
                Cette action va archiver le groupe et tous ses éléments associés :
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 mb-2">Éléments qui seront archivés :</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Le groupe "{groupeToArchive?.nom}"</li>
                  <li>• Tous les élèves de ce groupe</li>
                  <li>• Toutes les notes de ces élèves</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Conséquences :</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Ces éléments ne seront plus visibles dans l'application</li>
                  <li>• Ils ne comptent plus dans les statistiques</li>
                  <li>• Vous pourrez les restaurer à tout moment</li>
                </ul>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={confirmArchiveGroupe} 
                className="flex-1"
                style={{ backgroundColor: '#d97706', color: 'white' }}
                data-testid="confirm-archive-btn"
              >
                Confirmer l'archivage
              </Button>
              <Button 
                variant="outline" 
                onClick={cancelArchiveGroupe} 
                data-testid="cancel-archive-btn"
              >
                Annuler
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};


export default MesGroupes;
